import { Backdrop, CircularProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import db from '../../db/db'
import OnboardingService from '../../services/OnboardingService'
import { sessionState } from '../../state'

interface OnboardingDetectorProps {
  children: React.ReactNode
}

/**
 * OnboardingDetector - HOC/Guard component that detects if a user needs onboarding
 *
 * Checks if user has offices or pending invites, and if onboarding is already complete.
 * Redirects to /onboarding if needed, otherwise renders children.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 7.2, 9.2, 9.5, 10.3
 */
const OnboardingDetector: React.FC<OnboardingDetectorProps> = ({
  children
}) => {
  const session = useRecoilValue(sessionState)
  const navigate = useNavigate()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState<boolean>(true)

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Wait for authentication to complete
      if (session.isAuthenticating) {
        return
      }

      // If no user, allow access (auth guards will handle redirect to signin)
      if (!session.user || !session.user.uid) {
        setIsChecking(false)
        return
      }

      try {
        setIsChecking(true)

        // Fast path: Check localStorage first for onboarding completion
        // The onboarding wizard should only ever show once - localStorage is the primary gate
        const onboardingCompleteKey = `onboardingComplete_${session.user.uid}`
        if (localStorage.getItem(onboardingCompleteKey) === 'true') {
          setIsChecking(false)
          return
        }

        // Requirement 1.2: Check if user has at least one office association
        const hasOffices = session.offices && session.offices.length > 0

        // Requirement 1.2: Check if user has pending invites
        let hasPendingInvites = false
        if (
          session.user.email &&
          typeof session.user.email === 'string' &&
          session.user.email.trim() !== ''
        ) {
          const invites = await db.query({
            collection: 'invite',
            where: ['email', '==', session.user.email]
          })
          hasPendingInvites = invites.length > 0
        }

        // Check if user has a pending capacity warning (rejected from offices due to capacity)
        const hasCapacityWarning =
          localStorage.getItem('pendingCapacityWarning') !== null

        // Check if user was previously rejected due to capacity (persistent flag)
        const wasRejectedDueToCapacity =
          localStorage.getItem('wasRejectedDueToCapacity') === 'true'

        console.log('OnboardingDetector check:', {
          hasOffices,
          hasPendingInvites,
          hasCapacityWarning,
          wasRejectedDueToCapacity,
          officeCount: session.offices?.length || 0
        })

        // If user has capacity warning OR was previously rejected, and has no offices, skip onboarding
        // (they were rejected from invited offices and should see the warning on home page)
        // CHECK THIS FIRST before checking invites, because invites may have been deleted
        if ((hasCapacityWarning || wasRejectedDueToCapacity) && !hasOffices) {
          console.log('User was rejected due to capacity - skipping onboarding')
          setIsChecking(false)
          return
        }

        // Requirement 1.2: If user has offices or invites, skip onboarding
        // Also cache that onboarding is effectively complete (user has what they need)
        if (hasOffices || hasPendingInvites) {
          localStorage.setItem(onboardingCompleteKey, 'true')
          setIsChecking(false)
          return
        }

        // Requirement 1.3, 1.4: Check if onboarding is already complete from database
        const onboardingRecord = await OnboardingService.getUserOnboarding(
          session.user.uid
        )

        if (onboardingRecord && onboardingRecord.isComplete) {
          // Requirement 1.4: Don't redirect if onboarding is complete
          // Cache in localStorage so we never check again
          localStorage.setItem(onboardingCompleteKey, 'true')
          setIsChecking(false)
          return
        }

        // Requirement 1.1, 9.2: Redirect to /onboarding if user needs onboarding
        // Only redirect if not already on the onboarding page
        if (location.pathname !== '/onboarding') {
          navigate('/onboarding', { replace: true })
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error)
        // On error, default to not redirecting to avoid blocking user
      } finally {
        setIsChecking(false)
      }
    }

    checkOnboardingStatus()
  }, [
    session.isAuthenticating,
    session.user,
    session.offices,
    navigate,
    location.pathname
  ])

  // Show loading state while checking
  if (isChecking || session.isAuthenticating) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: 9999,
          position: 'fixed',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' size={60} />
      </Backdrop>
    )
  }

  // Render children (main app or onboarding page)
  return <>{children}</>
}

export default OnboardingDetector
